import { tap } from 'rxjs/operators';
import { ToastService } from '@sb-core/services/toast.service';
import { User } from '@sb-shared/models/user';
import { HttpWebApiService } from '@sb-shared/services/http-web-api.service';
import { Injectable } from '@angular/core';
import { HttpRequestSettings, apis, controllerTypes } from '@sb-shared/models/request-settings';
import { ChatSessionTypeId } from '../enums/chat-session-type';
import { AccessRequestEmailsRequest } from '../models/requests/access-request-emails-request';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MessagingService {
  urlBase = 'WebMessageCenter/';

  config: HttpRequestSettings = {
    api: apis.Web,
    successCallback: () => this.toast.emailSent()
  }

  activationStates = {
    Active: 'SB_Active',
    NotLoggedIn: 'SB_Not_logged_in',
    Inactive: 'SB_Inactive'
  }

  constructor(
    private http: HttpWebApiService,
    private toast: ToastService) {
  }

  sendAccessRequestEmails(request: AccessRequestEmailsRequest): Observable<any> {
    return this.http.post(`${this.urlBase}SendAccessRequestEmails`, request, this.config);
  }

  getActivationLabel(person) {
    if (person) {
      const status = person.personStatus;
      if (status === this.activationStates.Active) {
        return 'SB_Send_Password_Reset';
      }
      if (status === this.activationStates.NotLoggedIn) {
        return 'SB_Resend_Welcome_Message';
      }
      if (status === this.activationStates.Inactive) {
        return 'SB_Send_Welcome_Message';
      }
    }
    return '';
  }

  sendAccessRequestEmailsForOrganisation(organisationId: number, request: AccessRequestEmailsRequest): Observable<any> {
    if (request) {
      return this.http.post(`${this.urlBase}SendAccessRequestEmailsForOrganisation?organisationId=${organisationId}`, request, this.config);
    }

    return null;
  }

  getAccessRequestEmailsRequestForUsers(users: User[]): AccessRequestEmailsRequest {
    const request = new AccessRequestEmailsRequest();

    for (const user of users) {
      if (user.isStaff) {
        switch (user.personStatus) {
          case this.activationStates.Active:
            request.staffToResetPassword.push(user.id);
            break;
          case this.activationStates.NotLoggedIn:
            request.staffToReInvite.push(user.id);
            break;
          case this.activationStates.Inactive:
            request.staffToWelcome.push(user.id);
            break;
        }
      }

      if (user.isParent) {
        switch (user.personStatus) {
          case this.activationStates.Active:
            request.parentsToResetPassword.push(user.id);
            break;
          case this.activationStates.NotLoggedIn:
            request.parentsToReInvite.push(user.id);
            break;
          case this.activationStates.Inactive:
            request.parentsToWelcome.push(user.id);
            break;
        }
      }

      if (user.isPupil) {
        switch (user.personStatus) {
          case this.activationStates.Active:
            request.pupilsToResetPassword.push(user.id);
            break;
          case this.activationStates.NotLoggedIn:
            request.pupilsToReInvite.push(user.id);
            break;
          case this.activationStates.Inactive:
            request.pupilsToWelcome.push(user.id);
            break;
        }
      }
    }

    return request;
  }

  sendSingleAccessRequestEmail(person: User, orgId?: number) {
    const request: AccessRequestEmailsRequest = this.getAccessRequestEmailsRequestForUsers([person]);

    if (orgId) {
      return this.sendAccessRequestEmailsForOrganisation(
        orgId,
        request
      );
    }
    else {
      return this.sendAccessRequestEmails(request);
    }
  }

  static readonly recipientGroupType = {
    Club: 0,
    Team: 1,
    Person: 2,
    Event: 3,
    WholeSchool: 4,
    SubjectClasses: 5
  };

  static readonly messageMediumType = {
    Email: { id: 1, label: 'SB_Email_Iinc_Mobile_App_Notifications' },
    SMS: { id: 3, label: 'SB_SMS' }
  };

  private noToastConfig: HttpRequestSettings = {
    api: apis.Web,
    showSuccessToast: false
  };

  private coreUserConfig: HttpRequestSettings = {
    api: apis.Core,
    controllerType: controllerTypes.User,
    showSuccessToast: false
  };

  getCommunicationModule(): Observable<any> {
    return this.http.get('Module/CommunicationModule', this.coreUserConfig);
  }

  getChatFilters(isStaff, isParent) {
    const chatFilters = [
      ChatSessionTypeId.Notice,
      ChatSessionTypeId.News,
      ChatSessionTypeId.MatchReport,
      ChatSessionTypeId.Homework
    ];

    if (isStaff) {
      chatFilters.push(ChatSessionTypeId.StaffNotice);
    }

    if (isParent || isStaff) {
      chatFilters.push(ChatSessionTypeId.ParentNotice);
    }

    return chatFilters;
  }

  getPickerRestriction(): Observable<any> {
    return this.http.get(this.urlBase + 'GetPickerRestriction', this.noToastConfig);
  }

  getSettings(): Observable<any> {
    return this.http.get(this.urlBase + 'GetSettings', this.noToastConfig);
  }

  getMessageMediums(): Observable<any> {
    return this.http.get(this.urlBase + 'GetMessageMediums', this.noToastConfig);
  }

  sendNewMessage(message: any): Observable<any> {
    return this.http.post(this.urlBase + 'SendNewMessage', message, this.noToastConfig);
  }

  removeMessage(messageId: number): Observable<any> {
    return this.http.post(this.urlBase + 'RemoveMessage', messageId, this.noToastConfig);
  }

  getAllTags(): Observable<any> {
    return this.http.get(this.urlBase + 'GetTags', this.noToastConfig);
  }

  getRecipientsAndCreditCount(message: any): Observable<any> {
    return this.http.post(this.urlBase + 'GetRecipientsAndCreditCount', message, this.noToastConfig);
  }

  getThisOrganisationsSmsBalance(): Observable<any> {
    return this.http.get(this.urlBase + 'GetCurrentOrganisationSMSBalance', this.noToastConfig);
  }

  getMessageHistory(currentPage: number, pageSize: number): Observable<any> {
    return this.http.get(this.urlBase + 'GetMessageHistory', {
      ...this.noToastConfig,
      params: { currentPage, pageSize }
    });
  }

  /**
   * The CSV of one sent SMS's recipients. Fetched as a blob, not text: the server prefixes a UTF-8 BOM
   * when a name is not ASCII, and decoding to a string drops it, which makes Excel misread those names.
   */
  getMessageRecipientReport(messageId: number): Observable<Blob> {
    return this.http.get(this.urlBase + 'GetMessageRecipientReport', {
      ...this.noToastConfig,
      config: { responseType: 'blob' },
      params: { messageId }
    });
  }

  resetRecipients(): any {
    return {
      teams: [],
      clubs: [],
      people: [],
      events: [],
      eventPeople: [],
      eventGroupedPeople: [],
      wholeSchoolGroup: [],
      subjectClasses: []
    };
  }

  hasRecipientSelected(recipients: any): boolean {
    if (!recipients) {
      return false;
    }
    return (recipients.teams?.length > 0) ||
      (recipients.clubs?.length > 0) ||
      (recipients.people?.length > 0) ||
      (recipients.events?.length > 0) ||
      (recipients.eventGroupedPeople?.length > 0) ||
      (recipients.eventPeople?.length > 0) ||
      (recipients.wholeSchoolGroup?.length > 0) ||
      (recipients.subjectClasses?.length > 0);
  }

  selectedWholeSchool(recipients: any): boolean {
    return recipients?.wholeSchoolGroup?.length > 0;
  }

  validateSendMessage(body: string, recipients: any): { valid: boolean; errorTitle?: string; errorMessage?: string } {
    if (!body || body.length <= 0) {
      return { valid: false, errorTitle: 'SB_Unable_to_send_message', errorMessage: 'SB_Cannot_send_a_message_with_no_message_body' };
    }
    if (!this.hasRecipientSelected(recipients)) {
      return { valid: false, errorTitle: 'SB_Unable_to_send_message', errorMessage: 'SB_Cannot_send_a_message_with_no_recipients' };
    }
    return { valid: true };
  }

  prepareEventGroupDto(events: any[], eventGroupedPeople: any[], eventPersons: any[], calendarEventId: number): any[] {
    if (!events || !Array.isArray(events)) {
      events = [];
    }

    const eventGroups = [];
    if (Array.isArray(eventGroupedPeople)) {
      eventGroupedPeople.forEach(eventGroup =>
        eventGroups.push({ id: calendarEventId, memberType: eventGroup.memberType })
      );
    }

    const eventPeople = [];
    if (Array.isArray(eventPersons)) {
      eventPersons.forEach(eventPerson =>
        eventPeople.push({ id: calendarEventId, memberType: eventPerson.memberType, personId: eventPerson.id })
      );
    }

    return events.concat(eventGroups).concat(eventPeople);
  }

  prepareRecipientGroups(model: any, recipients: any, calendarEventId: number): void {
    if (!model.recipients) {
      model.recipients = [];
    }
    const rgt = MessagingService.recipientGroupType;
    model.recipients.push({ type: rgt.Club, recipients: recipients.clubs || [] });
    model.recipients.push({ type: rgt.Team, recipients: recipients.teams || [] });
    model.recipients.push({ type: rgt.Person, recipients: recipients.people || [] });
    model.recipients.push({
      id: calendarEventId,
      type: rgt.Event,
      recipients: this.prepareEventGroupDto(recipients.events, recipients.eventGroupedPeople, recipients.eventPeople, calendarEventId)
    });
    model.recipients.push({ type: rgt.WholeSchool, recipients: recipients.wholeSchoolGroup || [] });
    model.recipients.push({ type: rgt.SubjectClasses, recipients: recipients.subjectClasses || [] });
  }
}
